import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Role, User } from '@tips-crm/shared';
import { clearToken, errorMessage, fetchMeRaw, getToken, setToken } from '../api/client.js';
import { fetchCurrentUser, login as loginRequest } from '../api/endpoints.js';
import { isSsoHost, oidcEnabled, ssoLogout, userManager } from './oidc.js';
import { bridgeSsoSession, clearSsoMarker, isSsoSession, SSO_ERROR_KEY } from './ssoBridge.js';

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  hasRole: (...roles: Role[]) => boolean;
  // Server-computed permission (DB role + ZITADEL project roles). Gates the bulk
  // lead-distribution dashboard.
  canDistributeLeads: boolean;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // On an SSO host we always check the OIDC store first, so start in loading to avoid a
  // flash of the signed-out landing before the portal session is verified.
  const [loading, setLoading] = useState<boolean>(oidcEnabled || Boolean(getToken()));

  // On mount, hydrate the session. SSO sessions come from the TIPSSSO portal and are
  // rebuilt from the OIDC store; legacy (password) sessions hydrate from `/auth/me`.
  useEffect(() => {
    let cancelled = false;

    // Legacy (password) session: confirm the stored token against /auth/me.
    function hydrateLegacy() {
      if (!getToken()) {
        if (!cancelled) setLoading(false);
        return;
      }
      fetchCurrentUser()
        .then((u) => {
          if (!cancelled) setUser(u);
        })
        .catch(() => {
          if (!cancelled) {
            clearToken();
            setUser(null);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    if (oidcEnabled && userManager) {
      const um = userManager;

      // Bridge a portal session into the CRM, but VERIFY it against /auth/me before
      // trusting it. A ZITADEL identity with no matching CRM user (or a rejected token)
      // must NOT produce a fabricated session — that rendered the app, 401'd on data,
      // redirected to login, re-bridged, and reloaded forever. On failure we clear the
      // local OIDC + CRM session so the user lands signed-out (password login still works).
      const hydrateSso = (u: Parameters<typeof bridgeSsoSession>[0]) => {
        bridgeSsoSession(u); // stores the access token + sso marker
        fetchMeRaw()
          .then((real) => {
            if (!cancelled) {
              setUser(real);
              setLoading(false);
            }
          })
          .catch((err: unknown) => {
            if (cancelled) return;
            // Surface WHY (e.g. "your SSO sign-in has no CRM account") so the login page
            // can explain it, rather than silently dropping the user to a signed-out shell.
            try {
              sessionStorage.setItem(SSO_ERROR_KEY, errorMessage(err));
            } catch {
              /* ignore storage failures */
            }
            clearToken();
            clearSsoMarker();
            setUser(null);
            setLoading(false);
            void um.removeUser().catch(() => {});
          });
      };

      // Keep the user fresh across silent token renewals + on the callback's UserLoaded.
      const onLoaded = (u: Parameters<typeof bridgeSsoSession>[0]) => hydrateSso(u);
      um.events.addUserLoaded(onLoaded);

      um
        .getUser()
        .then((u) => {
          if (cancelled) return;
          if (u && !u.expired) {
            hydrateSso(u);
          } else if (window.location.pathname.startsWith('/auth/')) {
            // The OIDC redirect callback is mid-flight; its UserLoaded event will
            // hydrate the session. Stay in loading rather than redirecting away.
          } else {
            // No portal session — a legacy password session may still be active.
            hydrateLegacy();
          }
        })
        .catch(() => {
          if (!cancelled) hydrateLegacy();
        });

      return () => {
        cancelled = true;
        um.events.removeUserLoaded(onLoaded);
      };
    }

    hydrateLegacy();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await loginRequest(email, password);
    setToken(result.token);
    setUser(result.user);
    return result.user;
  }, []);

  const logout = useCallback(() => {
    const sso = isSsoSession();
    clearToken();
    clearSsoMarker();
    setUser(null);
    // SSO sessions end through the ZITADEL end-session flow (redirects to /welcome).
    if (oidcEnabled && sso) {
      void ssoLogout();
    }
  }, []);

  const hasRole = useCallback(
    (...roles: Role[]) => (user ? roles.includes(user.role) : false),
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      login,
      logout,
      hasRole,
      canDistributeLeads: user?.can_distribute_leads ?? false,
    }),
    [user, loading, login, logout, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="route-center">
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }
  if (!user) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    // Only the SSO host (tipscontact.com) shows the marketing landing with the portal
    // login; the other CRM domains keep going straight to the legacy password form.
    const dest = isSsoHost()
      ? `/welcome?redirect=${redirect}`
      : `/login?redirect=${redirect}`;
    return <Navigate to={dest} replace />;
  }
  return <>{children}</>;
}

/** Route guard keyed on the server-computed `can_distribute_leads` permission rather
 *  than a DB role, so a ZITADEL sales manager (whose CRM DB role is just `agent`) passes. */
export function LeadDistributionRoute({ children }: { children: ReactNode }) {
  const { user, loading, canDistributeLeads } = useAuth();
  if (loading) {
    return (
      <div className="route-center">
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!canDistributeLeads) {
    return (
      <div className="route-center">
        <div className="empty-state">
          <h2>Access denied</h2>
          <p>You do not have permission to distribute leads.</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

export function RoleRoute({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="route-center">
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) {
    return (
      <div className="route-center">
        <div className="empty-state">
          <h2>Access denied</h2>
          <p>Your role ({user.role}) does not have access to this page.</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
